import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
// #region --------------- Matter User Connection React Query hooks ----------------------

import {
  createMatterUserConnectionV1,
  deleteMatterUserConnectionV1,
  updateMatterUserConnectionsV1,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static";
import { type APIError } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

export const useCreateMatterUserConnection = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterUserConnectionV1>[0]) =>
      createMatterUserConnectionV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error creating matter user connection");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Matter user connection created successfully");
      }
    },
  });
};

export const useDeleteMatterUserConnection = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterUserConnectionV1>[0]) =>
      deleteMatterUserConnectionV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error deleting matter user connection");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Matter user connection deleted successfully");
      }
    },
  });
};

export const useUpdateMatterUserConnections = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterUserConnectionsV1>[0]) =>
      updateMatterUserConnectionsV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating matter user connections");
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Matter user connections updated successfully");
    },
  });
};
